import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Branch-level "Purchase Order Grouping Mode" setting.
 * - "vendor"   (default): one PO per vendor (legacy behaviour).
 * - "category" : single consolidated PO grouped by category.
 *
 * Loaded once when a branch_manager / cafe_owner logs in.
 * Exposes `mode` and `setMode(next)` which persists to backend and updates state.
 */
const POGroupingContext = createContext({ mode: "vendor", setMode: () => {}, loading: false });

export function POGroupingProvider({ children }) {
  const { user } = useAuth();
  const [mode, setModeState] = useState("vendor");
  const [loading, setLoading] = useState(false);

  // Only load for roles that can see PO data
  const eligible = user && (user.role === "branch_manager" || user.role === "cafe_owner");

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    setLoading(true);
    api.get("/branch/po-grouping").then((r) => {
      if (!cancelled) setModeState((r.data?.mode || "vendor"));
    }).catch(() => { /* keep default */ }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eligible, user?.id]);

  const setMode = useCallback(async (next) => {
    const m = (next || "vendor").toLowerCase();
    if (!["vendor", "category"].includes(m)) return;
    // Optimistic update
    setModeState(m);
    try {
      await api.put("/branch/po-grouping", { mode: m });
    } catch (e) {
      // Roll back if server rejects
      const prev = mode;
      setModeState(prev);
      throw e;
    }
  }, [mode]);

  return (
    <POGroupingContext.Provider value={{ mode, setMode, loading }}>
      {children}
    </POGroupingContext.Provider>
  );
}

export function usePOGrouping() {
  return useContext(POGroupingContext);
}
